import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LuChevronDown, LuMoon, LuSun, LuUser } from 'react-icons/lu'
import { fetchNotificationCounts } from '../services/notificationService.js'
import { useTheme } from '../context/ThemeContext.jsx'

const AuthControls = ({ onNavClose }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth0()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const [notificationCount, setNotificationCount] = useState(0)
  const { theme, toggleTheme } = useTheme()
  const isDark = theme === 'dark'

  const currentPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '/'

  useEffect(() => {
    if (!menuOpen) return undefined

    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [menuOpen])

  useEffect(() => {
    let cancelled = false
    const loadNotifications = async () => {
      if (!isAuthenticated) {
        setNotificationCount(0)
        return
      }
      try {
        const token = await getAccessTokenSilently()
        const data = await fetchNotificationCounts({ token })
        if (!cancelled) {
          setNotificationCount(Number(data?.total || 0))
        }
      } catch (error) {
        console.warn('Unable to load notifications', error)
        if (!cancelled) {
          setNotificationCount(0)
        }
      }
    }
    loadNotifications()
    const interval = setInterval(loadNotifications, 60 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [isAuthenticated, getAccessTokenSilently])

  const handleLogin = () =>
    loginWithRedirect({
      appState: { returnTo: currentPath },
    })

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
    setMenuOpen(false)
    onNavClose?.()
  }

  if (isLoading) {
    return (
      <button className="secondary-button" disabled>
        Connecting…
      </button>
    )
  }

  if (!isAuthenticated) {
    return (
      <button className="secondary-button" onClick={handleLogin}>
        Log in
      </button>
    )
  }

  const displayName = user?.given_name ?? user?.nickname ?? user?.name ?? 'Member'
  const trimmedName = displayName.length > 25 ? `${displayName.slice(0, 25)}...` : displayName

  return (
    <div
      className={`auth-menu ${menuOpen ? 'is-open' : ''}`}
      ref={menuRef}
      onMouseEnter={() => setMenuOpen(true)}
      onMouseLeave={() => setMenuOpen(false)}
    >
      <button
        type="button"
        className="auth-menu__trigger secondary-button"
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={menuOpen}
      >
        <LuUser aria-hidden="true" />
        <span className="auth-menu__label">{trimmedName}</span>
        {notificationCount > 0 && <span className="auth-menu__badge" aria-label="New notifications" />}
        <LuChevronDown aria-hidden="true" className="auth-menu__chevron" />
      </button>
      <div className="auth-menu__panel" role="menu">
        <Link to="/buy" className="auth-menu__item" role="menuitem" onClick={() => { setMenuOpen(false); onNavClose?.() }}>
          Buyer hub
        </Link>
        <Link to="/sell" className="auth-menu__item" role="menuitem" onClick={() => { setMenuOpen(false); onNavClose?.() }}>
          Manage listings
        </Link>
        <Link to="/calendar" className="auth-menu__item" role="menuitem" onClick={() => { setMenuOpen(false); onNavClose?.() }}>
          Calendar
        </Link>
        <Link to="/profile" className="auth-menu__item" role="menuitem" onClick={() => { setMenuOpen(false); onNavClose?.() }}>
          Edit profile
        </Link>
        <button type="button" className="auth-menu__item" role="menuitem" onClick={() => { toggleTheme(); onNavClose?.() }}>
          {isDark ? <LuSun aria-hidden="true" /> : <LuMoon aria-hidden="true" />}
          <span>{isDark ? 'Switch to light mode' : 'Switch to dark mode'}</span>
        </button>
        <button type="button" className="auth-menu__item is-danger" role="menuitem" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </div>
  )
}

export default AuthControls
